/**
 * Single source of truth for advisor auth state per ADR-039.
 *
 * Replaces the old `useAuth` hook that decoded a JWT out of
 * sessionStorage on every render. The token is now in an HttpOnly
 * cookie the browser cannot read; this context fetches the session
 * view from `GET /api/me` once on mount, exposes it to descendants,
 * and offers `refresh()` for AC3/AC4 (producer picker / mid-session
 * switch) which mutate `producerId` server-side.
 *
 * `login()` redirects to Cognito's hosted UI. `logout()` calls
 * `POST /api/auth/logout` so the session record is actually deleted
 * before the browser is redirected to Cognito's /logout.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  getLoginUrl,
  getLogoutUrl,
  type CanonicalRole,
  type RoleDefinition,
} from './constants.js';
import { apiFetch } from '../lib/api-fetch.js';

export interface ProducerOption {
  id: string;
  label: string;
  lastUsedAt: string | null;
}

/**
 * BLUF-1323 — one entry per role the identity holds. Rendered as a
 * tile on the role-picker page. Mirrors ProducerOption's shape.
 */
export interface RoleOption {
  name: CanonicalRole;
  label: string;
  description: string;
  precedence: number;
  lastUsedAt: string | null;
}

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * Set when /api/me returns a non-401 error (network down, 5xx).
   * Distinct from "unauthenticated": consumers should NOT bounce to
   * login on this — the user might already be signed in but the API
   * is sick. RequireAuth surfaces this as an error boundary; the
   * normal happy path keeps `error` null.
   */
  error: 'network' | 'server' | null;
  email: string | null;
  role: CanonicalRole | null;
  groups: CanonicalRole[];
  producerId: string | null;
  producerOptions: ProducerOption[];
  /**
   * BLUF-1323 — the role the user is currently acting as. Distinct
   * from `role` (derived-preferred at login). When multi-role, this
   * is null until the picker resolves it via PUT /api/me/role.
   */
  activeRole: CanonicalRole | null;
  /**
   * BLUF-1323 — every role the identity holds. Empty when the user
   * has exactly one role (picker is skipped).
   */
  roleOptions: RoleOption[];
  /**
   * BLUF-1323 — live role catalogue, fetched once alongside /api/me
   * so the picker page (BLUF-1323) can render tiles from data.
   * `null` while /api/auth/role-catalogue is still in flight or has
   * failed. Consumers must handle the null case; the picker shows a
   * loading state until this populates.
   */
  roleCatalogue: RoleDefinition[] | null;
  /**
   * BLUF-1323 — capability tags for the caller's effective role
   * (activeRole ?? role), parsed from Cognito group description
   * `[caps: ...]` markers. Consumed by `RequireCapability` so lazy
   * module bundles gate on capability instead of a hardcoded role
   * list. Empty array while /api/me is still loading or the role has
   * no marker (fail-closed — same shape the server-side gates use).
   */
  capabilities: string[];
  /**
   * BLUF-1323 — identification flag: true when the caller's effective
   * role is a Wawanesa-internal role (carries `[caps: internal]` on
   * its Cognito group description). NOT a flow driver — the login
   * flow branches on `producer-skip`. Consumers use this for UI hints
   * (e.g. "internal user" banner, telemetry) but must not use it to
   * gate access to modules or features.
   */
  isInternal: boolean;
}

export interface AuthContextValue extends AuthState {
  login: () => void;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  error: null,
  email: null,
  role: null,
  groups: [],
  producerId: null,
  producerOptions: [],
  activeRole: null,
  roleOptions: [],
  roleCatalogue: null,
  capabilities: [],
  isInternal: false,
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface MeResponse {
  email: string;
  role: CanonicalRole | null;
  groups: CanonicalRole[];
  producerId: string | null;
  producerOptions: ProducerOption[];
  activeRole: CanonicalRole | null;
  roleOptions: RoleOption[];
  capabilities: string[];
  isInternal: boolean;
}

type FetchMeResult =
  | { kind: 'ok'; me: MeResponse }
  | { kind: 'unauthenticated' }
  | { kind: 'network' }
  | { kind: 'server' };

async function fetchMe(): Promise<FetchMeResult> {
  let res: Response;
  try {
    res = await fetch('/api/me', { credentials: 'include' });
  } catch {
    return { kind: 'network' };
  }
  if (res.status === 401) return { kind: 'unauthenticated' };
  if (!res.ok) return { kind: 'server' };
  try {
    const me = (await res.json()) as MeResponse;
    return { kind: 'ok', me };
  } catch {
    return { kind: 'server' };
  }
}

/**
 * BLUF-1323 — fetch the live role catalogue. Best-effort: if it fails,
 * consumers fall back to whatever hardcoded role names they use for
 * gates; only the picker (BLUF-1323) is fully data-driven and shows
 * a loading state until this returns.
 */
async function fetchRoleCatalogue(): Promise<RoleDefinition[] | null> {
  try {
    const res = await fetch('/api/auth/role-catalogue', {
      credentials: 'include',
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { roles: RoleDefinition[] };
    return body.roles ?? null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(initialState);

  const refresh = useCallback(async () => {
    // BLUF-1323 — fetch /api/me and the role catalogue in parallel so
    // the picker doesn't wait on an extra sequential round trip. The
    // catalogue is auth-required; if /api/me is 401, the catalogue
    // will 401 too and resolve to null, which is fine.
    const [result, catalogue] = await Promise.all([
      fetchMe(),
      fetchRoleCatalogue(),
    ]);
    switch (result.kind) {
      case 'ok':
        setState({
          isAuthenticated: true,
          isLoading: false,
          error: null,
          email: result.me.email,
          role: result.me.role,
          groups: result.me.groups,
          producerId: result.me.producerId,
          producerOptions: result.me.producerOptions,
          activeRole: result.me.activeRole,
          roleOptions: result.me.roleOptions,
          roleCatalogue: catalogue,
          capabilities: result.me.capabilities ?? [],
          isInternal: result.me.isInternal ?? false,
        });
        return;
      case 'unauthenticated':
        setState({ ...initialState, isLoading: false });
        return;
      case 'network':
      case 'server':
        setState({ ...initialState, isLoading: false, error: result.kind });
        return;
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(() => {
    const url = getLoginUrl();
    if (url) window.location.href = url;
  }, []);

  const logout = useCallback(async () => {
    try {
      // Use apiFetch so the X-CSRF-Token header is attached. A raw fetch
      // would 403 against the global CSRF gate. skipAuthRedirect: a 401
      // here just means "already logged out" — no need to bounce to Cognito.
      await apiFetch('/api/auth/logout', {
        method: 'POST',
        skipAuthRedirect: true,
      });
    } catch {
      // ignore — the cookie is gone either way once we redirect
    }
    setState({ ...initialState, isLoading: false });
    const url = getLogoutUrl();
    if (url) window.location.href = url;
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within <AuthProvider>');
  }
  return ctx;
}
